import { isEolMoment } from 'utils/constants';

// Overview field config for decoupling type/options from values
// Used as fallback when form API is unavailable or returns no Overview fields.
// Do not list template-only fields (e.g. productZen, mvpStatus) here — those come
// only from createOverviewConfigFromFormApi when the server template includes them.
export const overviewFieldConfig = {
  headline: { type: 'richtext' },
  whatAreWeLaunching: { type: 'richtext' },
  whyAreWeLaunching: {
    type: 'dropdown',
    api: 'goals' // Fetch goal options from API instead of hardcoded options
  },
  howIsItDifferent: { type: 'richtext' },
  whereWillItLaunch: { type: 'richtext' },
  audienceStrategicAlignment: {
    type: 'audience_strategic_alignment' // Special type for two multi-select dropdowns + richtext
  },
  status: { type: 'text' },
  statusDescription: { type: 'richtext' }
};

/**
 * When the moment overview payload includes a field but the form template omits it,
 * merge these minimal type definitions so editors still work.
 */
export const overviewFieldTypeFallbacks: Record<
  string,
  { type: string; api?: string }
> = {
  productZen: { type: 'richtext' },
  mvpStatus: { type: 'dropdown_richtext', api: 'mvpStatus' }
};

/**
 * Rendering policies for Overview fields whose visibility should be driven by
 * the backend/template rather than by the presence of legacy payload keys.
 */
export const overviewFieldRenderPolicies: Record<
  string,
  { requiresTemplateConfig: boolean }
> = {
  productZen: { requiresTemplateConfig: true },
  mvpStatus: { requiresTemplateConfig: true }
};

/** End of Life (EOL) slideout overview — no goal, Product Zen, or MVP fields. */
export const EOL_EXCLUDED_OVERVIEW_FIELDS = [
  'goal',
  'whyAreWeLaunching',
  'productZen',
  'mvpStatus',
  'mvpDescription'
] as const;

/** EOL-specific display labels for overview fields that have launch-framed questions. */
export const EOL_OVERVIEW_FIELD_LABELS: Partial<Record<string, string>> = {
  whatAreWeLaunching: 'WHAT ARE WE SUNSETTING?',
  howIsItDifferent: 'HOW IS THIS BEING REPLACED?',
  whereWillItLaunch: 'REGIONS THIS WILL IMPACT?',
  audienceStrategicAlignment: 'WHO DOES THIS IMPACT?'
};

export function getOverviewFieldLabel(
  defaultLabel: string,
  field: string,
  momentType?: string | null
): string {
  if (isEolMoment(momentType)) {
    return EOL_OVERVIEW_FIELD_LABELS[field] ?? defaultLabel;
  }
  return defaultLabel;
}

export function omitEolExcludedOverviewFields<
  T extends Record<string, unknown>
>(config: T, momentType?: string | null): T {
  if (!isEolMoment(momentType)) {
    return config;
  }
  const next = { ...config };
  for (const key of EOL_EXCLUDED_OVERVIEW_FIELDS) {
    delete next[key];
  }
  return next;
}
